/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ViewVehicleListDialog from "./ViewVehicleListDialog";

function DeleteVehicleListDialog({
  open,
  onClose,
  onConfirm,
  vehicleToDelete,
  portfolioId,
}) {
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

DeleteVehicleListDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  vehicleToDelete: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
  }),
  portfolioId: PropTypes.string.isRequired,
};

  const handleViewVehicles = (e) => {
    e.stopPropagation(); // Prevent the delete dialog from closing
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          padding: '16px'
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1 }}>
        <DialogTitle sx={{ fontSize: '22px', fontWeight: 500, padding: 0 }}>
          Delete Vehicles List
        </DialogTitle>
        <IconButton onClick={onClose} sx={{ color: '#666' }}>
          ✕
        </IconButton>
      </Box>
      
      <DialogContent sx={{ padding: '0 8px', mb: 3 }}>
        <Typography sx={{ fontSize: '14px', mb: 3, color: '#555657', fontWeight: 400 }}>
          You are about to delete a vehicle list.
        </Typography>
        
        <Box sx={{ 
          border: '1px solid #e0e0e0', 
          px: 2,
          py: 1, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center'
        }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
              {vehicleToDelete?.name || 'Vehicle List Name'}
            </Typography>
            <Typography sx={{ color: '#666', fontSize: '14px' }}>
              {vehicleToDelete?.id || 'Vehicle ID goes here'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleViewVehicles}
            sx={{ 
              color: '#666',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                color: '#1976d2'
              }
            }}
          >
            <KeyboardArrowRightIcon sx={{ fontSize: '24px' }} />
          </IconButton>
        </Box>
        
        <Typography sx={{ fontSize: '14px', mt: 3, mb: 2, color: '#555657', fontWeight: 400 }}>
          This listing will be removed from the vehicles list. Are you sure you want to continue?
        </Typography>
        
        <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#555657' }}>
          That action can not be undone.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ padding: '0 8px', gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: '#e0e0e0',
            color: '#333',
            textTransform: 'none',
            borderRadius: '4px',
            px: 3,
            py: 0.5,
            height: '36px',
            border: '2px solid transparent',
            '&:hover': {
              backgroundColor: '#d0d0d0'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            onClose(); // Close dialog first
            onConfirm(); // Then execute delete
          }}
          sx={{
            backgroundColor: 'transparent',
            color: '#b50b00',
            border: '2px solid #f44336',
            textTransform: 'none',
            borderRadius: '4px',
            px: 3,
            py: 0.5,
            height: '36px',
            fontWeight: 500,
            fontSize: '14px',
            '&:hover': {
              backgroundColor: '#f44336',
              color: 'white'
            }
          }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>

    {/* View Vehicle List Dialog */}
    <ViewVehicleListDialog
      open={viewDialogOpen}
      onClose={handleCloseViewDialog}
      vehicleListName={vehicleToDelete?.name}
      portfolioId={portfolioId}
      vehicleListId={vehicleToDelete?.id}
    />
    </>
  );
}

export default DeleteVehicleListDialog;
